export type EnvKey = keyof ImportMetaEnv & `VITE_${string}`;

/** `@{Name}` is an unreplaced pipeline token; cmps shipped those as real values by mistake. */
const isMissing = (value: string | undefined): value is undefined =>
  value === undefined || value.trim() === '' || /^@\{.*\}$/.test(value.trim());

/**
 * Reads a variable from the runtime config first (`window._env_`, written to `/config.js` when the
 * container starts), then from the build-time `.env`. Empty and `@{…}` values count as missing.
 */
export const readEnv = (key: EnvKey): string | undefined => {
  const runtime = (globalThis as { _env_?: Partial<Record<EnvKey, string>> })._env_?.[key];
  if (!isMissing(runtime)) return runtime.trim();
  const buildTime = import.meta.env[key] as string | undefined;
  return isMissing(buildTime) ? undefined : buildTime.trim();
};

/** For values the app can't start without. Throws a message that names the variable. */
export const requireEnv = (key: EnvKey): string => {
  const value = readEnv(key);
  if (value === undefined)
    throw new Error(`Missing environment variable ${key} (see .env.example)`);
  return value;
};

const toPositiveInt = (value: string | undefined, fallback: number) => {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const toBoundedNumber = (value: string | undefined, fallback: number, min: number, max: number) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : fallback;
};

const toList = (value: string | undefined) =>
  (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

/**
 * Typed access to environment variables (see .env.example).
 * Everything here ships to the browser: never put secrets in VITE_* variables.
 */
export const env = {
  appName: readEnv('VITE_APP_NAME') ?? 'PermiOne',
  apiBaseUrl: readEnv('VITE_API_BASE_URL') ?? '/api',
  /** Per-request timeout for the CMPS API, in milliseconds. */
  apiTimeoutMs: toPositiveInt(readEnv('VITE_API_TIMEOUT_MS'), 30_000),
  /** Value of the `x-origin-strategy` header the CMPS BFF uses to identify this portal. */
  originStrategy: readEnv('VITE_ORIGIN_STRATEGY') ?? 'public-portal',
  useMocks: (readEnv('VITE_USE_MOCKS') ?? 'true') === 'true',
  isDev: import.meta.env.DEV,
  /** Entra External ID. Required values are checked in modules/account/auth/msalConfig.ts. */
  msal: {
    knownAuthorities: toList(readEnv('VITE_MSAL_KNOWN_AUTHORITIES')),
    redirectUri: readEnv('VITE_MSAL_CONFIG_AUTH_REDIRECT_URI'),
    postLogoutRedirectUri: readEnv('VITE_MSAL_CONFIG_AUTH_POST_LOGOUT_REDIRECT_URI') ?? '/',
  },
  /** ArcGIS MapView defaults (Washington, DC). Override per environment like cmps-public-web. */
  map: {
    centerLatitude: toBoundedNumber(readEnv('VITE_MAP_CENTER_LATITUDE'), 38.9072, -90, 90),
    centerLongitude: toBoundedNumber(readEnv('VITE_MAP_CENTER_LONGITUDE'), -77.0369, -180, 180),
    zoomLevel: toPositiveInt(readEnv('VITE_MAP_ZOOM_LEVEL'), 12),
    minZoomLevel: toPositiveInt(readEnv('VITE_MAP_MIN_ZOOM_LEVEL'), 8),
    basemap: readEnv('VITE_MAP_BASEMAP') ?? 'topo-vector',
  },
} as const;
